import { ArrowDropDown, ArrowDropUp, Label } from "@mui/icons-material";
import { ClickAwayListener, FormHelperText, InputAdornment, Paper, Popper, TextField } from "@mui/material";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { textFieldSX } from "../../constants/textFieldCSS";
import { SelectionOption } from "../../types/common";
import ThemedRadio from "./ThemedRadio";
import { Z_INDEX } from "../../constants/zIndex";

type SelectDropdownProps = {
    defaultSelected?: SelectionOption | SelectionOption[],
    selectionOptions: SelectionOption[],
    optionsLoading?: boolean;
    additionalClasses?: string;
    onOptionSelection: (selectedOptionId: string) => void;
    size?: "small" | "medium";
    disabled?: boolean;
    placeholder?: string;
    multiple?: boolean;
}

const SelectDropdown = ({
    defaultSelected,
    selectionOptions,
    optionsLoading,
    additionalClasses,
    onOptionSelection,
    size,
    disabled,
    placeholder,
    multiple = false
}: SelectDropdownProps) => {
    const [selectedOptions, setSelectedOptions] =
        useState<SelectionOption[]>(
            multiple && Array.isArray(defaultSelected) ? defaultSelected :
                (defaultSelected ? [defaultSelected as SelectionOption] : [])
        );
    const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<SelectionOption[]>(
        selectionOptions);
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredOptions(selectionOptions);
        } else {
            const lowerCaseQuery = searchQuery.toLowerCase();
            setFilteredOptions(
                selectionOptions.filter(option =>
                    option.title.toLowerCase().includes(lowerCaseQuery) ||
                    (option.subtitle && option.subtitle.toLowerCase().includes(lowerCaseQuery))
                )
            );
        }
    }, [searchQuery, selectionOptions]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const handleOptionClick = (selectionOption: SelectionOption) => {
        if (multiple) {
            const newSelection = selectedOptions.some(opt => opt.id === selectionOption.id)
                ? selectedOptions.filter(opt => opt.id !== selectionOption.id)
                : [...selectedOptions, selectionOption];
            setSelectedOptions(newSelection);
            setSearchQuery(newSelection.map(opt => opt.title).join(', '));
            onOptionSelection(newSelection.map(opt => opt.id).join(','));
        } else {
            setSelectedOptions([selectionOption]);
            setSearchQuery(selectionOption.title);
            onOptionSelection(selectionOption.id);
        }
        handleClose();
    };

    const handleFocus = (event: React.MouseEvent<HTMLDivElement>) => {
        setIsTextFieldFocused(true);
        setSearchQuery('');
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setIsTextFieldFocused(false);
        setSearchQuery('');
        setAnchorEl(null);
    };

    function formatTextForLabel(text: string): string {
        // Replace any uppercase character that follows a lowercase character or a digit 
        // with a space and that uppercase character.
        return text.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
    }

    useEffect(() => {
        if (defaultSelected) {
            if (Array.isArray(defaultSelected)) {
                setSelectedOptions(defaultSelected);
                setSearchQuery(defaultSelected.map(opt => opt.title).join(', '));
            } else {
                setSelectedOptions([defaultSelected]);
                setSearchQuery(defaultSelected.title);
            }
        }
    }, [!Array.isArray(defaultSelected) ? defaultSelected?.title : defaultSelected.map(opt => opt.title).join(',')]);

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <div className={clsx("w-full relative", additionalClasses)}>
                <TextField
                    fullWidth
                    value={isTextFieldFocused
                        ? searchQuery
                        : selectedOptions.map(opt => formatTextForLabel(opt.title)).join(', ')}
                    placeholder={placeholder}
                    onClick={handleFocus}
                    onChange={isTextFieldFocused ? handleInputChange : undefined}
                    sx={{
                        ...textFieldSX
                    }}
                    InputProps={{
                        endAdornment: !disabled && <InputAdornment position="end">{
                            isTextFieldFocused ?
                                <ArrowDropUp />
                                : <ArrowDropDown />
                        }</InputAdornment>
                    }}
                    // Disable browser text assistance features. This is distracting.
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    size={size}
                    disabled={disabled}
                />
                <Popper
                    open={isTextFieldFocused && Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ width: anchorEl?.offsetWidth }}
                    sx={{ zIndex: Z_INDEX.DROPDOWN }}
                >
                    <Paper 
                        className={'p-1 rounded-lg'}
                        sx={{
                            boxShadow: "0px 4px 14px 0px rgba(0, 0, 0, 0.12)",
                            maxHeight: "300px",
                            overflowY: "auto",
                        }}
                    >
                        {
                            optionsLoading ?
                                <FormHelperText>
                                    Loading...
                                </FormHelperText>
                            : filteredOptions.length > 0 ?
                                filteredOptions.map(selectionOption => {
                                    const isOptionSelected = multiple
                                        ? selectedOptions.some(opt => opt.id === selectionOption.id)
                                        : (selectionOption.id === selectedOptions[0]?.id);
                                    return (
                                        <div
                                            key={selectionOption.id}
                                            className={clsx("flex items-center cursor-pointer p-3",
                                                "my-1 hover:bg-backgroundGrey rounded-lg",
                                                {"bg-backgroundGrey": isOptionSelected})}
                                            onClick={() => handleOptionClick(selectionOption)}
                                        >
                                            <ThemedRadio checked={isOptionSelected} size="small" />
                                            <div>
                                                <div className="text-xs font-semibold">{formatTextForLabel(selectionOption.title)}</div>
                                                {selectionOption.subtitle && <FormHelperText>{selectionOption.subtitle}</FormHelperText>}
                                            </div>
                                        </div>
                                    )
                                })
                            : <FormHelperText>No options found</FormHelperText>
                        }
                    </Paper>
                </Popper>
            </div>
        </ClickAwayListener>
    )
}

export default SelectDropdown;
